import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Inbox, Search, Mail, Phone, MessageCircle, User, Package,
  CheckCircle2, XCircle, Trash2, Loader2, Send, BarChart3,
  TrendingUp, PieChart, FileText, Plus, Pencil, Star, Reply,
  Download, Clock, AlertTriangle, Settings, CheckSquare, Square, Zap, Target, Ticket
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Inbox, dotColor: 'bg-blue-500' },
  contacted: { label: 'Contacted', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: CheckCircle2, dotColor: 'bg-amber-500' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-500 border-slate-200', icon: XCircle, dotColor: 'bg-slate-400' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isEmail(str) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str); }
function isPhone(str) { return /^[\d\s+\-()]{7,}$/.test(str.replace(/\s/g, '')); }


// ═══════════════════════════════════════════════
// Inquiry Detail Modal
// ═══════════════════════════════════════════════
function InquiryDetailModal({ open, onClose, inquiry, onStatusChange, onDelete, templates }) {
  const [note, setNote] = useState('');
  const [updating, setUpdating] = useState(false);
  const [replying, setReplying] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  if (!inquiry) return null;
  const contact = inquiry.visitor_contact || '';
  const contactIsEmail = isEmail(contact);
  const contactIsPhone = isPhone(contact);
  const sc = STATUS_CONFIG[inquiry.status] || STATUS_CONFIG.new;

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/api/sales/inquiries/${inquiry.id}/status?status=${newStatus}`, null, {
        params: note ? { note } : {},
      });
      onStatusChange(inquiry.id, newStatus, note);
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
    } catch { toast.error('Failed to update status'); }
    finally { setUpdating(false); }
  };

  const handleReply = async () => {
    if (!contactIsEmail) return;
    setReplying(true);
    try {
      const params = selectedTemplate ? `?template_id=${selectedTemplate}` : '';
      await axios.post(`${API}/api/sales/inquiries/${inquiry.id}/reply${params}`);
      toast.success(`Reply sent to ${contact}`);
      onStatusChange(inquiry.id, 'contacted', null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    }
    finally { setReplying(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    try {
      await axios.delete(`${API}/api/sales/inquiries/${inquiry.id}`);
      onDelete(inquiry.id);
      onClose();
      toast.success('Inquiry deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="inquiry-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-emerald-500" />
            Inquiry Details
          </DialogTitle>
          <DialogDescription>From {inquiry.visitor_name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="flex items-center gap-2">
            <Badge className={cn("text-xs border", sc.color)}>{sc.label}</Badge>
            <span className="text-xs text-gray-400">{timeAgo(inquiry.created_at)}</span>
            {inquiry.replied_at && <Badge className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200">Replied</Badge>}
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-semibold text-gray-900" data-testid="inquiry-visitor-name">{inquiry.visitor_name}</span>
            </div>
            <div className="flex items-center gap-2">
              {contactIsEmail ? <Mail className="h-4 w-4 text-blue-400" /> : <Phone className="h-4 w-4 text-emerald-400" />}
              <span className="text-sm text-gray-600" data-testid="inquiry-visitor-contact">{contact}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Package className="h-4 w-4 text-purple-400" />
            <span>Re: <span className="font-medium text-gray-700">{inquiry.product_name}</span></span>
          </div>

          {inquiry.message && (
            <div className="p-4 rounded-xl bg-white border border-slate-200">
              <p className="text-xs text-gray-400 mb-1 uppercase tracking-wider font-medium">Message</p>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap" data-testid="inquiry-message">{inquiry.message}</p>
            </div>
          )}

          {inquiry.note && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
              <p className="text-xs text-amber-600 font-medium mb-0.5">Your Note</p>
              <p className="text-sm text-amber-800">{inquiry.note}</p>
            </div>
          )}

          {/* Quick Reply Actions */}
          <div>
            <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider font-medium">Quick Reply</p>
            <div className="flex gap-2 flex-wrap">
              {contactIsEmail && (
                <a href={`mailto:${contact}?subject=Re: Inquiry about ${encodeURIComponent(inquiry.product_name)}&body=${encodeURIComponent(`Hi ${inquiry.visitor_name},\n\nThank you for your interest in ${inquiry.product_name}.\n\n`)}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors" data-testid="reply-email">
                  <Mail className="h-3.5 w-3.5" /> Email
                </a>
              )}
              {contactIsPhone && (
                <>
                  <a href={`tel:${contact}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors" data-testid="reply-call">
                    <Phone className="h-3.5 w-3.5" /> Call
                  </a>
                  <a href={`https://wa.me/${contact.replace(/[^0-9+]/g, '')}?text=${encodeURIComponent(`Hi ${inquiry.visitor_name}, thank you for your inquiry about ${inquiry.product_name}!`)}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-medium hover:bg-green-100 transition-colors" data-testid="reply-whatsapp">
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Auto-Reply via Template */}
          {contactIsEmail && templates.length > 0 && (
            <div className="p-4 rounded-xl bg-emerald-50/50 border border-emerald-100 space-y-2">
              <p className="text-xs text-emerald-700 font-medium uppercase tracking-wider">Auto-Reply via Template</p>
              <div className="flex gap-2">
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger className="flex-1 h-8 text-xs bg-white" data-testid="reply-template-select">
                    <SelectValue placeholder="Select template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} {t.is_default ? '(Default)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 text-xs h-8" onClick={handleReply} disabled={replying} data-testid="send-template-reply">
                  {replying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Reply className="h-3.5 w-3.5" />}
                  {replying ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          )}

          {/* Note + Status */}
          <div className="space-y-2 pt-2 border-t">
            <Textarea placeholder="Add a note (optional)..." value={note} onChange={e => setNote(e.target.value)} rows={2} className="text-sm" data-testid="inquiry-note" />
            <div className="flex gap-2">
              {inquiry.status !== 'contacted' && (
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white gap-1 text-xs flex-1" onClick={() => handleStatusChange('contacted')} disabled={updating} data-testid="mark-contacted">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Contacted
                </Button>
              )}
              {inquiry.status !== 'closed' && (
                <Button size="sm" variant="outline" className="gap-1 text-xs flex-1" onClick={() => handleStatusChange('closed')} disabled={updating} data-testid="mark-closed">
                  <XCircle className="h-3.5 w-3.5" /> Close
                </Button>
              )}
              {inquiry.status === 'closed' && (
                <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white gap-1 text-xs flex-1" onClick={() => handleStatusChange('new')} disabled={updating} data-testid="reopen-inquiry">
                  <Inbox className="h-3.5 w-3.5" /> Reopen
                </Button>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" size="sm" className="text-blue-600 hover:text-blue-700 text-xs gap-1" onClick={async () => {
            try {
              const res = await axios.post(`${API}/api/sales/inquiries/${inquiry.id}/convert-to-ticket`);
              toast.success('Converted to support ticket');
              onStatusChange(inquiry.id, 'escalated', null);
              onClose();
            } catch (e) { toast.error(e.response?.data?.detail || 'Failed to convert'); }
          }} data-testid="convert-to-ticket-btn">
            <Ticket className="h-3.5 w-3.5" /> Convert to Ticket
          </Button>
          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 text-xs gap-1" onClick={handleDelete} data-testid="delete-inquiry">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════
// Analytics Tab
// ═══════════════════════════════════════════════
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/sales/inquiry-analytics`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 text-blue-500 animate-spin" /></div>;
  if (!data) return null;

  const maxProduct = Math.max(...(data.by_product || []).map(p => p.count), 1);
  const maxDaily = Math.max(...(data.daily_trend || []).map(d => d.count), 1);

  return (
    <div className="space-y-5" data-testid="analytics-tab">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Inquiries', value: data.total, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-100', icon: Inbox },
          { label: 'Response Rate', value: `${data.response_rate}%`, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100', icon: TrendingUp },
          { label: 'Avg Response', value: data.avg_response_hours != null ? `${data.avg_response_hours}h` : 'N/A', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100', icon: CheckCircle2 },
          { label: 'New (Pending)', value: data.status_counts?.new || 0, color: 'text-red-600', bg: 'bg-red-50 border-red-100', icon: Package },
        ].map(s => (
          <Card key={s.label} className={cn("border", s.bg)}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={cn("h-5 w-5", s.color)} />
              <div>
                <p className={cn("text-2xl font-bold", s.color)}>{s.value}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Daily Trend */}
        <Card className="border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="h-4 w-4 text-blue-500" />Daily Trend (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {(data.daily_trend || []).length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No data yet</p>
            ) : (
              <div className="flex items-end gap-1 h-32" data-testid="daily-trend-chart">
                {(data.daily_trend || []).map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      {d.date}: {d.count}
                    </div>
                    <div
                      className="w-full bg-blue-400 rounded-t hover:bg-blue-500 transition-colors"
                      style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: '4px' }}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* By Product */}
        <Card className="border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><PieChart className="h-4 w-4 text-purple-500" />Top Products</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data.by_product || []).length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No data yet</p>
            ) : (
              (data.by_product || []).map((p, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`product-stat-${i}`}>
                  <span className="text-sm text-gray-700 flex-1 truncate">{p.product}</span>
                  <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-400 rounded-full" style={{ width: `${(p.count / maxProduct) * 100}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-600 w-8 text-right">{p.count}</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown + Contact Type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Card className="border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(data.status_counts || {}).map(([key, val]) => {
              const sc = STATUS_CONFIG[key];
              if (!sc) return null;
              const pct = data.total > 0 ? Math.round((val / data.total) * 100) : 0;
              return (
                <div key={key} className="flex items-center gap-3">
                  <Badge className={cn("text-[10px] border w-20 justify-center", sc.color)}>{sc.label}</Badge>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={cn("h-full rounded-full", sc.dotColor)} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-600 w-12 text-right">{val} ({pct}%)</span>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Contact Type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: 'Email', count: data.contact_types?.email || 0, icon: Mail, color: 'text-blue-500' },
              { label: 'Phone', count: data.contact_types?.phone || 0, icon: Phone, color: 'text-emerald-500' },
            ].map(t => (
              <div key={t.label} className="flex items-center gap-3">
                <t.icon className={cn("h-4 w-4", t.color)} />
                <span className="text-sm text-gray-700 flex-1">{t.label}</span>
                <span className="text-sm font-bold text-gray-900">{t.count}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* SLA Performance */}
      <SLAAnalyticsSection />
    </div>
  );
}

/* ── SLA Analytics Section ──────────────────────────────────────────── */
function SLAAnalyticsSection() {
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/api/sales/inquiry-sla-analytics`)
      .then(r => setSla(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 text-blue-500 animate-spin" /></div>;
  if (!sla) return null;
  return (
    <div className="space-y-4" data-testid="sla-analytics">
      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Target className="h-4 w-4 text-red-500" />SLA Performance (Target: {sla.sla_hours}h)</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Breach Rate', value: `${sla.breach_rate}%`, color: sla.breach_rate > 20 ? 'text-red-600' : 'text-emerald-600', bg: sla.breach_rate > 20 ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100', icon: AlertTriangle },
          { label: 'Avg Response', value: `${sla.avg_response_hours}h`, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-100', icon: Clock },
          { label: 'Median Response', value: `${sla.median_response_hours}h`, color: 'text-violet-600', bg: 'bg-violet-50 border-violet-100', icon: Zap },
          { label: 'P90 Response', value: `${sla.p90_response_hours}h`, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100', icon: TrendingUp },
        ].map(s => (
          <Card key={s.label} className={cn("border", s.bg)}>
            <CardContent className="p-3 flex items-center gap-2">
              <s.icon className={cn("h-4 w-4", s.color)} />
              <div>
                <p className={cn("text-lg font-bold", s.color)}>{s.value}</p>
                <p className="text-[9px] text-gray-500 uppercase">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {sla.weekly_trend?.length > 0 && (
        <Card className="border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Weekly SLA Trend</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-20">
              {sla.weekly_trend.map((w, i) => {
                const maxW = Math.max(...sla.weekly_trend.map(x => x.total), 1);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="w-full flex flex-col gap-[1px]" style={{ height: `${Math.max((w.total / maxW) * 100, 4)}%`, minHeight: '4px' }}>
                      {w.breached > 0 && <div className="bg-red-400 rounded-t" style={{ flex: w.breached }} />}
                      <div className="bg-emerald-400 flex-1" />
                    </div>
                    <span className="text-[8px] text-gray-400">{w.week}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 mt-2 text-[9px] text-gray-400">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-400 rounded" />On Time</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-400 rounded" />Breached</span>
            </div>
          </CardContent>
        </Card>
      )}
      {Object.keys(sla.breached_by_product || {}).length > 0 && (
        <Card className="border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">SLA Breaches by Product</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(sla.breached_by_product).map(([product, count]) => (
              <div key={product} className="flex items-center justify-between text-sm">
                <span className="text-gray-700 truncate">{product}</span>
                <Badge className="bg-red-50 text-red-600 border-red-200 text-[10px]">{count} breach{count !== 1 ? 'es' : ''}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════
// Templates & Settings Tab
// ═══════════════════════════════════════════════
function TemplatesTab({ templates, onRefresh }) {
  const [editing, setEditing] = useState(null); // null or template obj
  const [form, setForm] = useState({ name: '', subject: '', body: '', is_default: false });
  const [saving, setSaving] = useState(false);
  const [sla, setSla] = useState({ hours: 24, enabled: true });
  const [slaLoading, setSlaLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/sales/inquiry-sla`).then(r => setSla(r.data)).catch(() => {}).finally(() => setSlaLoading(false));
  }, []);

  const saveSla = async (newSla) => {
    setSla(newSla);
    try {
      await axios.put(`${API}/api/sales/inquiry-sla`, newSla);
      toast.success('SLA settings saved');
    } catch { toast.error('Failed to save SLA'); }
  };

  const openNew = () => {
    setForm({ name: '', subject: '', body: '', is_default: false });
    setEditing({ id: null });
  };

  const openEdit = (t) => {
    setForm({ name: t.name, subject: t.subject, body: t.body, is_default: t.is_default });
    setEditing(t);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error('All fields are required');
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/api/sales/inquiry-templates/${editing.id}`, form);
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/api/sales/inquiry-templates`, form);
        toast.success('Template created');
      }
      setEditing(null);
      onRefresh();
    } catch { toast.error('Failed to save template'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/api/sales/inquiry-templates/${id}`);
      toast.success('Template deleted');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-4" data-testid="templates-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Auto-Respond Templates</h3>
          <p className="text-xs text-gray-400 mt-0.5">Use variables: {`{{visitor_name}}`}, {`{{product_name}}`}, {`{{company_name}}`}</p>
        </div>
        <Button size="sm" className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={openNew} data-testid="create-template-btn">
          <Plus className="h-3.5 w-3.5" /> New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="p-10 text-center">
            <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-gray-800 mb-1">No Templates Yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">Create a template to auto-respond to inquiries. Set one as default for automatic replies.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {templates.map(t => (
            <Card key={t.id} className={cn("border hover:shadow-md transition-all", t.is_default && "border-emerald-200 bg-emerald-50/30")} data-testid={`template-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-gray-900">{t.name}</h4>
                      {t.is_default && <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200"><Star className="h-2.5 w-2.5 mr-0.5" />Default</Badge>}
                    </div>
                    <p className="text-xs text-gray-500 mb-1"><span className="font-medium">Subject:</span> {t.subject}</p>
                    <p className="text-xs text-gray-400 line-clamp-2">{t.body}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}>
                      <Pencil className="h-3.5 w-3.5 text-gray-400" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(t.id)} data-testid={`delete-template-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Template' : 'New Template'}</DialogTitle>
            <DialogDescription>Variables: {`{{visitor_name}}`}, {`{{product_name}}`}, {`{{company_name}}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input placeholder="Template name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="text-sm" data-testid="template-name" />
            <Input placeholder="Email subject line" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="text-sm" data-testid="template-subject" />
            <Textarea placeholder="Email body..." value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={6} className="text-sm" data-testid="template-body" />
            <div className="flex items-center gap-2">
              <Switch checked={form.is_default} onCheckedChange={v => setForm(f => ({ ...f, is_default: v }))} data-testid="template-default-switch" />
              <span className="text-xs text-gray-600">Set as default (auto-reply to new inquiries)</span>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SLA Alert Settings */}
      <div className="mt-6 pt-6 border-t">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <h3 className="text-sm font-semibold text-gray-900">SLA Alert Settings</h3>
        </div>
        <Card className="border">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Auto-alert on overdue inquiries</p>
                <p className="text-xs text-gray-400">Get notified if an inquiry is not responded within the set time</p>
              </div>
              {!slaLoading && <Switch checked={sla.enabled} onCheckedChange={v => saveSla({ ...sla, enabled: v })} data-testid="sla-toggle" />}
            </div>
            {sla.enabled && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">Alert after</span>
                <Select value={String(sla.hours)} onValueChange={v => saveSla({ ...sla, hours: parseInt(v) })}>
                  <SelectTrigger className="w-28 h-8 text-xs" data-testid="sla-hours">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="2">2 hours</SelectItem>
                    <SelectItem value="4">4 hours</SelectItem>
                    <SelectItem value="8">8 hours</SelectItem>
                    <SelectItem value="12">12 hours</SelectItem>
                    <SelectItem value="24">24 hours</SelectItem>
                    <SelectItem value="48">48 hours</SelectItem>
                    <SelectItem value="72">72 hours</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-gray-500">of no response</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════
export default function InquiryInboxPage() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({ new: 0, contacted: 0, closed: 0, total: 0 });
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkReplyOpen, setBulkReplyOpen] = useState(false);
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkBody, setBulkBody] = useState('');
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [bulkSending, setBulkSending] = useState(false);

  const fetchInquiries = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/api/sales/inquiries`, { params });
      setInquiries(Array.isArray(data) ? data : []);
    } catch { toast.error('Unable to load inquiries'); }
    finally { setLoading(false); }
  }, [search, filterStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sales/inquiries/stats`);
      setStats(data);
    } catch {}
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sales/inquiry-templates`);
      setTemplates(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  useEffect(() => { fetchInquiries(); fetchStats(); fetchTemplates(); }, [fetchInquiries, fetchStats, fetchTemplates]);

  const handleStatusChange = (id, newStatus, note) => {
    setInquiries(prev => prev.map(i => i.id === id ? { ...i, status: newStatus, note: note || i.note } : i));
    setSelectedInquiry(prev => prev?.id === id ? { ...prev, status: newStatus, note: note || prev.note } : prev);
    fetchStats();
  };

  const handleDelete = (id) => {
    setInquiries(prev => prev.filter(i => i.id !== id));
    fetchStats();
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selected.size === inquiries.length) setSelected(new Set());
    else setSelected(new Set(inquiries.map(i => i.id)));
  };
  const handleBulkStatus = async (status) => {
    if (selected.size === 0) return;
    try {
      await axios.post(`${API}/api/sales/inquiries/bulk-status`, { inquiry_ids: [...selected], status });
      toast.success(`${selected.size} inquiries updated to ${status}`);
      setSelected(new Set());
      fetchInquiries();
      fetchStats();
    } catch { toast.error('Bulk update failed'); }
  };
  const handleBulkReply = async () => {
    if (selected.size === 0) return;
    setBulkSending(true);
    try {
      const res = await axios.post(`${API}/api/sales/inquiries/bulk-reply`, {
        inquiry_ids: [...selected],
        template_id: bulkTemplateId,
        custom_subject: bulkSubject,
        custom_body: bulkBody,
      });
      toast.success(`Sent: ${res.data.sent}, Failed: ${res.data.failed}`);
      setBulkReplyOpen(false);
      setSelected(new Set());
      setBulkSubject('');
      setBulkBody('');
      setBulkTemplateId('');
      fetchInquiries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk reply failed'); }
    finally { setBulkSending(false); }
  };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="inquiry-inbox-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
          <Inbox className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Inquiry Inbox<PageInfoTooltip pageKey="inquiry-inbox" /></h1>
          <p className="text-xs text-gray-400">{stats.total} total · {stats.new} new · {stats.contacted} contacted</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="inbox" className="space-y-4">
        <TabsList>
          <TabsTrigger value="inbox" className="gap-1.5"><Inbox className="h-3.5 w-3.5" />Inbox {stats.new > 0 && <Badge className="h-4 px-1 text-[9px] bg-blue-500 text-white">{stats.new}</Badge>}</TabsTrigger>
          <TabsTrigger value="analytics" className="gap-1.5"><BarChart3 className="h-3.5 w-3.5" />Analytics</TabsTrigger>
          <TabsTrigger value="templates" className="gap-1.5"><FileText className="h-3.5 w-3.5" />Templates</TabsTrigger>
        </TabsList>

        {/* Export buttons — always visible */}
        <div className="flex gap-1.5 ml-auto">
          {['csv', 'docx', 'pdf'].map(fmt => (
            <button
              key={fmt}
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/api/sales/inquiries/export/${fmt}`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `inquiries.${fmt}`;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success(`Exported as ${fmt.toUpperCase()}`);
                } catch { toast.error('Export failed'); }
              }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-xs text-gray-600 font-medium transition-colors"
              data-testid={`export-${fmt}`}
            >
              <Download className="h-3 w-3" />{fmt.toUpperCase()}
            </button>
          ))}
        </div>

        {/* ─── Inbox Tab ─── */}
        <TabsContent value="inbox">
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <div className="relative flex-1 min-w-[140px] max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 h-8 text-sm" data-testid="inquiry-search" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="inquiry-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              { key: 'new', label: 'New', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-100', icon: Inbox },
              { key: 'contacted', label: 'Contacted', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100', icon: CheckCircle2 },
              { key: 'closed', label: 'Closed', color: 'text-slate-500', bg: 'bg-slate-50 border-slate-100', icon: XCircle },
            ].map(s => (
              <Card key={s.key} className={cn("border cursor-pointer hover:shadow-md transition-shadow", s.bg, filterStatus === s.key && 'ring-2 ring-offset-1 ring-blue-400')}
                onClick={() => setFilterStatus(filterStatus === s.key ? 'all' : s.key)} data-testid={`stat-${s.key}`}>
                <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
                  <s.icon className={cn("h-4 w-4 sm:h-5 sm:w-5", s.color)} />
                  <div>
                    <p className={cn("text-lg sm:text-2xl font-bold", s.color)}>{stats[s.key]}</p>
                    <p className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-wider">{s.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Bulk Action Bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg mb-3" data-testid="bulk-action-bar">
              <Badge className="bg-blue-500 text-white text-xs">{selected.size} selected</Badge>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setBulkReplyOpen(true)} data-testid="bulk-reply-btn">
                <Send className="h-3 w-3 mr-1" />Bulk Reply
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleBulkStatus('contacted')}>
                <CheckCircle2 className="h-3 w-3 mr-1" />Mark Contacted
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleBulkStatus('closed')}>
                <XCircle className="h-3 w-3 mr-1" />Close
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-400" onClick={() => setSelected(new Set())}>Clear</Button>
            </div>
          )}

          {/* Inquiry List */}
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 text-blue-500 animate-spin" /></div>
          ) : inquiries.length === 0 ? (
            <Card className="border-dashed border-2 border-gray-300 bg-slate-50/50">
              <CardContent className="p-12 text-center">
                <Inbox className="h-12 w-12 text-blue-300 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-800 mb-1">No Inquiries Yet</h3>
                <p className="text-sm text-gray-500 max-w-sm mx-auto">When visitors submit inquiries from your product showcase, they will appear here.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {/* Select All */}
              <div className="flex items-center gap-2 px-2 py-1">
                <button onClick={selectAll} className="text-gray-400 hover:text-gray-600" data-testid="select-all-btn">
                  {selected.size === inquiries.length && inquiries.length > 0 ? <CheckSquare className="h-4 w-4 text-blue-500" /> : <Square className="h-4 w-4" />}
                </button>
                <span className="text-[10px] text-gray-400">{selected.size === inquiries.length && inquiries.length > 0 ? 'Deselect all' : 'Select all'}</span>
              </div>
              {inquiries.map(inq => {
                const sc = STATUS_CONFIG[inq.status] || STATUS_CONFIG.new;
                const contact = inq.visitor_contact || '';
                const isSelected = selected.has(inq.id);
                return (
                  <Card key={inq.id} className={cn("border hover:shadow-md transition-all cursor-pointer group", inq.status === 'new' && 'border-l-4 border-l-blue-500 bg-blue-50/30', isSelected && 'ring-2 ring-blue-400')}
                    onClick={() => setSelectedInquiry(inq)} data-testid={`inquiry-row-${inq.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <button onClick={(e) => { e.stopPropagation(); toggleSelect(inq.id); }} className="mt-1 flex-shrink-0" data-testid={`select-${inq.id}`}>
                          {isSelected ? <CheckSquare className="h-4 w-4 text-blue-500" /> : <Square className="h-4 w-4 text-gray-300" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <h3 className={cn("text-sm font-semibold truncate", inq.status === 'new' ? 'text-gray-900' : 'text-gray-600')}>{inq.visitor_name}</h3>
                              <Badge className={cn("text-[9px] border flex-shrink-0", sc.color)}>{sc.label}</Badge>
                              {inq.replied_at && <Badge className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200">Replied</Badge>}
                            </div>
                            <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">{timeAgo(inq.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-3 mb-1.5">
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              {isEmail(contact) ? <Mail className="h-3 w-3" /> : <Phone className="h-3 w-3" />}{contact}
                            </span>
                            <span className="text-xs text-gray-400 flex items-center gap-1"><Package className="h-3 w-3" />{inq.product_name}</span>
                          </div>
                          {inq.message && <p className="text-xs text-gray-500 line-clamp-1">{inq.message}</p>}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                          {isEmail(contact) && (
                            <a href={`mailto:${contact}`} className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 hover:bg-blue-100 transition-colors" title="Email">
                              <Mail className="h-3.5 w-3.5" />
                            </a>
                          )}
                          {isPhone(contact) && (
                            <a href={`https://wa.me/${contact.replace(/[^0-9+]/g, '')}`} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center text-green-600 hover:bg-green-100 transition-colors" title="WhatsApp">
                              <MessageCircle className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ─── Analytics Tab ─── */}
        <TabsContent value="analytics">
          <AnalyticsTab />
        </TabsContent>

        {/* ─── Templates Tab ─── */}
        <TabsContent value="templates">
          <TemplatesTab templates={templates} onRefresh={fetchTemplates} />
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      <InquiryDetailModal
        open={!!selectedInquiry}
        onClose={() => setSelectedInquiry(null)}
        inquiry={selectedInquiry}
        onStatusChange={handleStatusChange}
        onDelete={handleDelete}
        templates={templates}
      />

      {/* Bulk Reply Dialog */}
      <Dialog open={bulkReplyOpen} onOpenChange={setBulkReplyOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-reply-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4" />Bulk Reply ({selected.size} inquiries)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-gray-700">Use Template</label>
              <Select value={bulkTemplateId} onValueChange={v => {
                setBulkTemplateId(v);
                const tpl = templates.find(t => t.id === v);
                if (tpl) { setBulkSubject(tpl.subject || ''); setBulkBody(tpl.body || ''); }
              }}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select template (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Custom Message</SelectItem>
                  {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Subject *</label>
              <Input value={bulkSubject} onChange={e => setBulkSubject(e.target.value)} placeholder="Re: Your inquiry about {{product}}" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Body *</label>
              <Textarea value={bulkBody} onChange={e => setBulkBody(e.target.value)} rows={4} placeholder="Dear {{name}},&#10;&#10;Thank you for your inquiry..." className="mt-1" />
              <p className="text-[10px] text-gray-400 mt-1">Use {'{{name}}'} and {'{{product}}'} for personalization</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkReplyOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkReply} disabled={bulkSending || !bulkSubject || !bulkBody} data-testid="send-bulk-reply-btn">
              {bulkSending ? 'Sending...' : `Send to ${selected.size} inquiries`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
